import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Star } from 'lucide-react';
import { getDishImageUrl } from '../../../services/api';
import { useCart } from '../../../contexts/CartContext';
import { getDisplayRating } from '../../../utils/ratingStars';
import DishPopup from '../../restaurant/DishPopup';
import namkeFallback from '../../../assets/namke-fallback.png';

const formatPrice = (price) => `${(Number(price) || 0).toFixed(2)} €`;

const FeaturedDishCard = ({ dish, onCardClick, onPlusClick }) => {
  const { t } = useTranslation();
  const [imgError, setImgError] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const { addToCart } = useCart();
  const imageUrl = !imgError ? (getDishImageUrl(dish) || namkeFallback) : namkeFallback;
  const rating = getDisplayRating(dish, 0);
  const reviewCount = Number(dish?.review_count) || 0;
  const badgeLabel = dish.discount_percentage ? `-${dish.discount_percentage}%` : dish.is_promoted ? t('home.promo') : null;
  const restaurantName = dish?.restaurant?.name || dish?.restaurant_name || null;
  const restaurantCity = dish?.restaurant?.city || dish?.restaurant_city || null;

  const handlePlusClick = (e) => {
    e.stopPropagation();
    setShowPopup(true);
    onPlusClick?.(dish);
  };

  return (
    <>
      <article
        style={{
          background: '#FCFBF5',
          borderRadius: '22px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 2px 10px rgba(5,16,4,0.05)',
          cursor: 'pointer',
        }}
        onClick={() => onCardClick?.(dish)}
      >
        <div style={{ position: 'relative' }}>
          <img
            src={imageUrl}
            alt={dish.name}
            style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
            loading="lazy"
            decoding="async"
            onError={() => setImgError(true)}
          />
          {badgeLabel && (
            <span
              style={{
                position: 'absolute',
                top: '14px',
                left: '14px',
                background: '#EF4C23',
                color: '#FCFBF5',
                fontFamily: 'Archivo, sans-serif',
                fontSize: '11.5px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                padding: '6px 13px',
                borderRadius: '999px',
              }}
            >
              {badgeLabel}
            </span>
          )}
        </div>
        <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <strong
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontWeight: 600,
                fontSize: '19px',
                lineHeight: 1.15,
                color: '#051004',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {dish.name}
            </strong>
            {reviewCount > 0 && (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '3px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, color: '#054625', whiteSpace: 'nowrap' }}>
                <Star size={12} style={{ fill: '#FFC717', color: '#FFC717' }} />
                {rating.toFixed(1)}
              </span>
            )}
          </div>
          {(restaurantName || restaurantCity) && (
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.66)' }}>
              {[restaurantName, restaurantCity].filter(Boolean).join(' · ')}
            </span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 'auto', paddingTop: '10px' }}>
            <span style={{ fontWeight: 600, fontSize: '17px', color: '#051004' }}>{formatPrice(dish.price)}</span>
            <button
              type="button"
              onClick={handlePlusClick}
              style={{
                marginLeft: 'auto',
                border: 'none',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                borderRadius: '999px',
                background: '#EF4C23',
                color: '#FCFBF5',
                display: 'grid',
                placeItems: 'center',
              }}
              aria-label={t('home.addToCart')}
            >
              <Plus size={18} />
            </button>
          </div>
        </div>
      </article>
      <DishPopup
        dish={dish}
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        onAddToCart={(cartItem) => {
          addToCart(cartItem);
          setShowPopup(false);
        }}
      />
    </>
  );
};

export default FeaturedDishCard;
